/**
 * Shared config + presentational atoms for the Storage onboarding wizard.
 * Extracted to keep StorageOnboardingWizard.tsx lean.
 */
import { useState } from 'react';
import { Copy, Check, KeyRound } from 'lucide-react';
import { Button, Input } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import type { NewAccessKey } from '@/api/s3/_shared';

/** Region new buckets are created in — matches the Wasabi zone (ap-southeast-1). */
export const STORAGE_REGION = 'ap-southeast-1';

export type PolicyScope = 'read' | 'readwrite';

export interface StorageStep {
  id: 'activate' | 'bucket' | 'policy' | 'credential' | 'review';
  label: string;
  title: string;
  description: string;
  skippable: boolean;
}

export const STORAGE_STEPS: StorageStep[] = [
  {
    id: 'activate',
    label: 'Activate',
    title: 'Activate Object Storage',
    description: 'Provision your storage service — a dedicated storage account is created for you.',
    skippable: false,
  },
  {
    id: 'bucket',
    label: 'Bucket',
    title: 'Create your first bucket',
    description: 'Buckets hold your objects. You can skip this and create one later.',
    skippable: true,
  },
  {
    id: 'policy',
    label: 'Policy',
    title: 'Add an access policy',
    description: 'Define what a credential is allowed to do. Skip to grant full access instead.',
    skippable: true,
  },
  {
    id: 'credential',
    label: 'Credential',
    title: 'Create an access key',
    description: 'Generate an S3 access key + secret for your apps. The secret is shown only once.',
    skippable: true,
  },
  {
    id: 'review',
    label: 'Done',
    title: 'Your storage is ready',
    description: 'Review what was set up and jump into the console.',
    skippable: false,
  },
];

/**
 * Build a managed IAM policy document scoped to one bucket (or all buckets when
 * none was created). read → get/list; readwrite → full S3 on the bucket.
 */
export function buildPolicyDoc(bucket: string | undefined, scope: PolicyScope): string {
  const resource = bucket
    ? [`arn:aws:s3:::${bucket}`, `arn:aws:s3:::${bucket}/*`]
    : ['arn:aws:s3:::*'];
  const Action =
    scope === 'read'
      ? ['s3:GetObject', 's3:ListBucket', 's3:GetBucketLocation']
      : ['s3:*'];
  return JSON.stringify(
    { Version: '2012-10-17', Statement: [{ Effect: 'Allow', Action, Resource: resource }] },
    null,
    2,
  );
}

/** One copyable read-only credential field. */
function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Field label={label}>
      {(props) => (
        <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
          <Input {...props} readOnly value={value} style={{ fontFamily: 'var(--vn-font-mono, monospace)' }} />
          <Button
            type="button"
            variant="secondary"
            startIcon={copied ? <Check size={14} /> : <Copy size={14} />}
            onClick={() => {
              void navigator.clipboard?.writeText(value);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
      )}
    </Field>
  );
}

/**
 * One-time reveal of a freshly created access key. Before creation shows a
 * prompt; after, the id + secret with a "save it now" warning.
 */
export function CredentialReveal({ newKey }: { newKey: NewAccessKey | null }) {
  if (!newKey) {
    return (
      <p style={{ color: 'var(--vn-fg-muted)', margin: 0, display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
        <KeyRound size={16} /> Create an S3 access key + secret to connect your apps and tools.
      </p>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Alert tone="warning" title="Save your secret key now">
        The secret access key is shown only once and cannot be retrieved later. Store it somewhere safe.
      </Alert>
      <CopyField label="Access key ID" value={newKey.access_key_id} />
      <CopyField label="Secret access key" value={newKey.secret_access_key} />
    </div>
  );
}
